import { useRef, useState, type ChangeEvent } from "react";
import { Cropper, type CropperRef } from "react-advanced-cropper";
import "react-advanced-cropper/dist/style.css";
import * as Dialog from "@radix-ui/react-dialog";
import ActionButton from "@/components/ui/action-button";

interface ImageUploadPreviewProps {
	label: string;
	value?: File | null;
	preview: string | null;
	onChange: (file: File | null, preview: string | null) => void;
	error?: string;
	maxSizeMB?: number;
	maxOutputSize?: number;
	icon?: "user" | "building";
	isDark?: boolean;
	freeAspect?: boolean;
}

const validateFile = (file: File, maxSizeMB: number): string | null => {
	const maxSize = maxSizeMB * 1024 * 1024;
	const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];

	if (!allowedTypes.includes(file.type)) {
		return "Please upload a valid image file (JPEG, PNG, GIF, or WebP)";
	}
	if (file.size > maxSize) {
		return `Image must be less than ${maxSizeMB}MB`;
	}
	return null;
};

export default function ImageUploadPreview({
	label,
	preview,
	onChange,
	error,
	maxSizeMB = 5,
	maxOutputSize = 256,
	icon = "user",
	isDark = false,
	freeAspect = false,
}: ImageUploadPreviewProps) {
	const inputRef = useRef<HTMLInputElement>(null);
	const cropperRef = useRef<CropperRef>(null);
	const [cropDialogOpen, setCropDialogOpen] = useState(false);
	const [rawImageUrl, setRawImageUrl] = useState<string | null>(null);
	const [selectedFile, setSelectedFile] = useState<File | null>(null);

	const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			const validationError = validateFile(file, maxSizeMB);
			if (validationError) {
				onChange(null, null);
				return;
			}

			const objectUrl = URL.createObjectURL(file);
			setRawImageUrl(objectUrl);
			setSelectedFile(file);
			setCropDialogOpen(true);
		}
	};

	const handleCropConfirm = () => {
		const cropper = cropperRef.current;
		if (cropper) {
			const canvas = cropper.getCanvas({
				maxWidth: maxOutputSize,
				maxHeight: maxOutputSize,
			});
			if (canvas) {
				canvas.toBlob((blob) => {
					if (blob && selectedFile) {
						const croppedFile = new File(
							[blob],
							selectedFile.name,
							{
								type: selectedFile.type,
							},
						);
						if (preview) {
							URL.revokeObjectURL(preview);
						}
						const croppedPreviewUrl =
							URL.createObjectURL(croppedFile);
						onChange(croppedFile, croppedPreviewUrl);
					}
				}, selectedFile?.type || "image/jpeg");
			}
		}
		cleanupCropState();
	};

	const handleCropCancel = () => {
		cleanupCropState();
		if (inputRef.current) {
			inputRef.current.value = "";
		}
	};

	const cleanupCropState = () => {
		if (rawImageUrl) {
			URL.revokeObjectURL(rawImageUrl);
		}
		setRawImageUrl(null);
		setSelectedFile(null);
		setCropDialogOpen(false);
	};

	const handleZoomIn = () => {
		cropperRef.current?.zoomImage(1.1);
	};

	const handleZoomOut = () => {
		cropperRef.current?.zoomImage(0.9);
	};

	const handleRemove = () => {
		if (preview) {
			URL.revokeObjectURL(preview);
		}
		onChange(null, null);
		if (inputRef.current) {
			inputRef.current.value = "";
		}
	};

	return (
		<div className="flex flex-col h-full">
			<label
				className={`block text-sm font-medium mb-2 ${isDark ? "text-white" : "text-t-primary"}`}
				style={{
					fontFamily: "var(--font-body)",
					fontWeight: "var(--font-body-weight)",
				}}
			>
				{label}
			</label>
			<input
				ref={inputRef}
				type="file"
				accept="image/jpeg,image/png,image/gif,image/webp"
				onChange={handleFileChange}
				className="hidden"
			/>

			{preview ? (
				<div className={`inline-flex flex-col items-start gap-2`}>
					<div
						className={`${freeAspect ? "w-full flex justify-center" : ""}`}
					>
						<div
							className={`relative inline-block py-4 ${freeAspect ? "w-full" : "w-32.5 aspect-square"}`}
						>
							<div
								className={`absolute inset-0 rounded-xl ${isDark ? "checkered-bg-dark" : "checkered-bg"}`}
							/>
							<img
								src={preview}
								alt="Preview"
								className={`relative object-contain mx-auto ${freeAspect ? `max-w-[${maxOutputSize}px] max-h-[${maxOutputSize}px]` : "w-full h-full"}`}
							/>
						</div>
					</div>
					<button
						type="button"
						onClick={handleRemove}
						className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-500 transition-colors"
						style={{
							fontFamily: "var(--font-body)",
							fontWeight: "var(--font-body-weight)",
						}}
					>
						<svg
							className="w-3.5 h-3.5"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
							strokeWidth={2}
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M6 18L18 6M6 6l12 12"
							/>
						</svg>
						Remove
					</button>
				</div>
			) : (
				<button
					type="button"
					onClick={() => inputRef.current?.click()}
					className={`${freeAspect ? "w-full py-6" : "w-32.5 aspect-square"} border border-dashed rounded-xl flex items-center justify-center transition-all ${
						isDark
							? "border-gray-700 bg-gray-800/50 hover:border-gray-600"
							: "border-gray-300 hover:border-gray-400 hover:bg-gray-50"
					}`}
				>
					<svg
						className={`${icon === "building" ? "w-8 h-8" : "w-10 h-10"} ${isDark ? "text-gray-600" : "text-gray-300"}`}
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
						strokeWidth={1.5}
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							d={
								icon === "user"
									? "M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
									: "M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z"
							}
						/>
					</svg>
				</button>
			)}

			{error && <p className="text-xs text-red-500 mt-1.5">{error}</p>}

			<Dialog.Root
				open={cropDialogOpen}
				onOpenChange={(open) => {
					if (!open) handleCropCancel();
				}}
			>
				<Dialog.Portal>
					<Dialog.Overlay className="fixed inset-0 bg-black/60 z-50" />
					<Dialog.Content
						className={`fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90vw] max-w-md rounded-2xl p-5 shadow-xl ${
							isDark ? "bg-gray-900" : "bg-white"
						}`}
					>
						<Dialog.Title
							className={`text-base font-semibold mb-4 ${
								isDark ? "text-white" : "text-gray-900"
							}`}
							style={{
								fontFamily: "var(--font-body)",
								fontWeight: "var(--font-body-weight)",
							}}
						>
							Crop Image
						</Dialog.Title>
						<div
							className={`w-full h-72 rounded-lg overflow-hidden flex items-center gap-2 ${
								isDark ? "bg-gray-800" : "bg-gray-100"
							}`}
						>
							<div className="h-full w-full relative">
								{rawImageUrl && (
									<Cropper
										ref={cropperRef}
										src={rawImageUrl}
										className="h-full w-full"
										backgroundClassName={
											isDark
												? "checkered-bg-dark"
												: "checkered-bg"
										}
										stencilProps={
											freeAspect
												? {}
												: {
														aspectRatio: 1,
													}
										}
									/>
								)}
								<div className="flex flex-col gap-2 absolute bottom-3 right-3">
									<button
										type="button"
										onClick={handleZoomIn}
										className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
											isDark
												? "bg-gray-700 hover:bg-gray-600 text-white"
												: "bg-gray-200 hover:bg-gray-300 text-gray-700"
										}`}
										aria-label="Zoom in"
									>
										<svg
											className="w-5 h-5"
											fill="none"
											viewBox="0 0 24 24"
											stroke="currentColor"
											strokeWidth={2}
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M12 4v16m8-8H4"
											/>
										</svg>
									</button>
									<button
										type="button"
										onClick={handleZoomOut}
										className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
											isDark
												? "bg-gray-700 hover:bg-gray-600 text-white"
												: "bg-gray-200 hover:bg-gray-300 text-gray-700"
										}`}
										aria-label="Zoom out"
									>
										<svg
											className="w-5 h-5"
											fill="none"
											viewBox="0 0 24 24"
											stroke="currentColor"
											strokeWidth={2}
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M20 12H4"
											/>
										</svg>
									</button>
								</div>
							</div>
						</div>
						<div className="flex gap-3 mt-4">
							<ActionButton
								variant="secondary"
								isDarkMode={isDark}
								onClick={handleCropCancel}
								className="flex-1"
							>
								Cancel
							</ActionButton>
							<ActionButton
								onClick={handleCropConfirm}
								className="flex-1"
							>
								Crop & Select
							</ActionButton>
						</div>
					</Dialog.Content>
				</Dialog.Portal>
			</Dialog.Root>
		</div>
	);
}
